import { useCallback, useEffect, useState } from "react";

import { msg } from "@lingui/core/macro";

import { toast } from "@readai/common/hooks/useToasts";
import log from "@readai/common/logger";

import useTimeout from "./useTimeout";

const showToastError = () => toast.error(msg`Couldn't copy to your clipboard`);

const useCopyToClipboard = (tooltipTimeout = 1000) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [supportsCopyToClipboard, setSupportsCopyToClipboard] = useState(
    Boolean(navigator?.clipboard),
  );

  useEffect(() => {
    // don't check permissions if we can't, or if we already know copy isn't supported
    if (!navigator || !navigator.permissions || !supportsCopyToClipboard) {
      return;
    }

    try {
      navigator.permissions
        .query({ name: "clipboard-write" as PermissionName })
        .then((result) => {
          setSupportsCopyToClipboard(result.state === "granted");
        })
        .catch(() => {
          // this permission only exists on Chromium browsers
          log.debug(
            "'clipboard-write' is not a permission name in this browser",
          );
        });
    } catch {
      log.debug("permission querying is not supported in this browser");
    }
  }, []);

  useTimeout(() => setCopied(false), tooltipTimeout, copied);

  const copyToClipboard = useCallback(
    (text: string, html?: string) => {
      if (supportsCopyToClipboard) {
        const copyTextFallback = () =>
          navigator.clipboard
            .writeText(text)
            .then(() => setCopied(true))
            .catch((err: unknown) => {
              showToastError();
              throw err;
            });

        if (window.ClipboardItem) {
          const textBlob = new Blob([text], { type: "text/plain" });
          const htmlBlob = html
            ? new Blob([html], { type: "text/html" })
            : undefined;

          const clipboardItem = htmlBlob
            ? new ClipboardItem({
                "text/plain": textBlob,
                "text/html": htmlBlob,
              })
            : new ClipboardItem({ [textBlob.type]: textBlob });

          navigator.clipboard
            .write([clipboardItem])
            .then(() => setCopied(true))
            .catch((err: unknown) => {
              if (err instanceof Error && err.name === "NotAllowedError") {
                log.error("Clipboard write permission denied");
                // Try fallback
                return copyTextFallback();
              } else {
                showToastError();
                throw err;
              }
            });
        } else {
          copyTextFallback();
        }
      } else {
        toast.warning(
          msg`Your browser doesn't support copying to your clipboard`,
        );
      }
    },
    [supportsCopyToClipboard],
  );

  return {
    copied,
    copyToClipboard,
    isSupported: supportsCopyToClipboard,
    disableCopy: !supportsCopyToClipboard,
  };
};

export default useCopyToClipboard;
